import { useCallback } from 'react';
import {
  useCheckNewNotificationsQuery,
  useMarkNotificationViewedMutation,
  useRemoveNotificationMutation,
  useNotificationSummaryQuery,
} from './api';

/**
 * Hook to fetch notifications with feature registration
 *
 * @param options - Query options including polling interval, skip, etc.
 */
export function useGetNotifications(options?: {
  pollingInterval?: number;
  refetchOnFocus?: boolean;
  skip?: boolean;
}) {
  return useCheckNewNotificationsQuery(undefined, {
    pollingInterval: options?.pollingInterval,
    refetchOnFocus: options?.refetchOnFocus,
    skip: options?.skip,
  });
}

/**
 * Hook to mark a notification as viewed
 */
export function useMarkNotificationViewed() {
  const [markNotificationViewed, result] = useMarkNotificationViewedMutation();

  const mutate = useCallback(
    (notificationId: string, options?: { onSuccess?: () => void; onError?: () => void }) => {
      const promise = markNotificationViewed(notificationId);

      // Handle options manually for consistent interface with other hooks
      if (options) {
        promise
          .unwrap()
          .then(() => options.onSuccess?.())
          .catch(() => options.onError?.());
      }

      return promise;
    },
    [markNotificationViewed],
  );

  return {
    mutate,
    ...result,
  };
}

/**
 * Hook to remove a notification
 */
export function useRemoveNotification() {
  const [removeNotification, result] = useRemoveNotificationMutation();

  const mutate = useCallback(
    (notificationId: string, options?: { onSuccess?: () => void; onError?: () => void }) => {
      const promise = removeNotification(notificationId);

      // Handle options manually for consistent interface with other hooks
      if (options) {
        promise
          .unwrap()
          .then(() => options.onSuccess?.())
          .catch(() => options.onError?.());
      }

      return promise;
    },
    [removeNotification],
  );

  return {
    mutate,
    ...result,
  };
}

/**
 * Hook for notifications with unified interface
 *
 * Combines all notification functionality in a single hook for convenience
 * while still ensuring feature registration.
 */
export function useNotifications(options?: { pollingInterval?: number; refetchOnFocus?: boolean }) {
  // Use RTK Query hooks
  const { data: notifications = [] } = useCheckNewNotificationsQuery(undefined, {
    pollingInterval: options?.pollingInterval || 10 * 60 * 1000, // Default: 10 minutes
    refetchOnFocus: options?.refetchOnFocus || false,
  });

  const { mutate: markOpened } = useMarkNotificationViewed();
  const { mutate: remove } = useRemoveNotification();

  // Calculate unread count
  const unreadCount = notifications.filter(n => n.isOpened === 0).length;

  return {
    notifications,
    unreadCount,
    markOpened,
    remove,
  };
}

const NOTIFICATION_SUMMARY_POLL_INTERVAL_MS = 3 * 60 * 1000; // 3 minutes

/**
 * Hook to poll the notification summary endpoint for badge counts.
 * Designed to be called from app-level components (Nav) so polling runs globally.
 * RTK Query deduplicates subscriptions, so multiple consumers share one poll.
 */
export function useNotificationSummaryPolling(options?: { skip?: boolean }) {
  const { data, isLoading, isFetching } = useNotificationSummaryQuery(undefined, {
    pollingInterval: NOTIFICATION_SUMMARY_POLL_INTERVAL_MS,
    refetchOnFocus: true,
    skip: options?.skip,
  });

  const cosignQueueCount = data?.items?.find(i => i.key === 'cosign_queue')?.count ?? 0;
  const editsRequestedCount = data?.items?.find(i => i.key === 'edits_requested')?.count ?? 0;
  const reopenedNeedingResignCount =
    data?.items?.find(i => i.key === 'reopened_needing_resign')?.count ?? 0;
  const totalCount = data?.total_count ?? 0;
  // data.total_count sums across all categories (notes, prescriptions, …). Badge
  // surfaces attached to the Notes nav want only the note-queue items.
  const notesTotalCount =
    data?.items
      ?.filter(i => (i as { category?: string }).category === 'note_queues')
      .reduce((sum, i) => sum + (i.count ?? 0), 0) ?? 0;
  // Self-serve scheduling queue (booking prospects + change requests). The BE
  // only returns these items to org-calendar managers, so the count is 0 for
  // everyone else and the Schedule nav badge stays hidden. Cast to a string
  // category like notesTotalCount above so this doesn't depend on the generated
  // enum carrying 'scheduling' before the BE deploys (GDN-2329).
  const schedulingTotalCount =
    data?.items
      ?.filter(i => (i as { category?: string }).category === 'scheduling')
      .reduce((sum, i) => sum + (i.count ?? 0), 0) ?? 0;

  return {
    cosignQueueCount,
    editsRequestedCount,
    reopenedNeedingResignCount,
    notesTotalCount,
    schedulingTotalCount,
    totalCount,
    isLoading,
    isFetching,
  };
}

export type PrescribingNotificationKey =
  | 'transmission_error_count'
  | 'pending_prescription_count'
  | 'refill_request_count'
  | 'change_request_count';

export interface PrescribingNotificationItem {
  key: PrescribingNotificationKey;
  count: number;
}

// Priority order matches backend _PRESCRIPTION_ROWS and the GDN-1062 spec:
// transmission failures first, then proxy-queued, refills, change requests last.
const PRESCRIBING_PRIORITY: PrescribingNotificationKey[] = [
  'transmission_error_count',
  'pending_prescription_count',
  'refill_request_count',
  'change_request_count',
];

/**
 * Hook for the prescribing notifications bell. Subscribes to the same
 * notification summary query as useNotificationSummaryPolling — RTK Query
 * deduplicates the request, so this adds no extra HTTP traffic.
 *
 * Returns only non-zero items in priority order. The bell consumer is
 * responsible for the additional gate of `isUserConnected` to DoseSpot.
 */
export function usePrescribingNotifications(options?: { skip?: boolean }) {
  const { data, isLoading, isFetching } = useNotificationSummaryQuery(undefined, {
    pollingInterval: NOTIFICATION_SUMMARY_POLL_INTERVAL_MS,
    refetchOnFocus: true,
    skip: options?.skip,
  });

  const counts: Record<PrescribingNotificationKey, number> = {
    transmission_error_count: 0,
    pending_prescription_count: 0,
    refill_request_count: 0,
    change_request_count: 0,
  };

  for (const item of data?.items ?? []) {
    if (item.category !== 'prescriptions') continue;
    if (item.key in counts) {
      counts[item.key as PrescribingNotificationKey] = item.count ?? 0;
    }
  }

  const items: PrescribingNotificationItem[] = PRESCRIBING_PRIORITY.filter(
    key => counts[key] > 0,
  ).map(key => ({ key, count: counts[key] }));

  const totalCount = items.reduce((sum, item) => sum + item.count, 0);

  return {
    items,
    totalCount,
    transmissionFailureCount: counts.transmission_error_count,
    isLoading,
    isFetching,
  };
}
